import type { ExperimentDefinition } from "./types";

// The experiment registry — the only file a page author edits to add or retire a
// test. Typed `as const satisfies Record<string, ExperimentDefinition>` so that
// each entry is validated against the shape *without* widening its variant names
// to `string`: that is what lets `useVariant("drive_highlights")` return the
// literal union `"accordion" | "carousel"` and turn a typo into a compile error.
export const EXPERIMENTS = {
  drive_highlights: {
    // Deliberately `draft`, not `running`. Version A's item copy is still
    // placeholder ("This is where the description goes." in 3 of its 4 items), so
    // real traffic must not be bucketed yet — the test would compare finished
    // copy against FPO text rather than the two layouts. `draft` still assigns in
    // localdev and staging (see ALLOW_DRAFT below) so the slice is demonstrable
    // now; flip to `running` once A's copy lands.
    status: "draft",
    version: 1,
    // Matched locale-aware: /es/products/drive and /fr-CA/products/drive
    // normalise to this same entry. All three locales are in scope.
    routes: ["/products/drive"],
    // `accordion` first = control (the treatment live on the PDP today).
    // Declaration order is also the `?ab=` letter: `?ab=a` = accordion,
    // `?ab=b` = carousel. Reordering these variants changes what `a`/`b` force.
    variants: { accordion: 50, carousel: 50 },
  },
} as const satisfies Record<string, ExperimentDefinition>;

export type ExperimentKey = keyof typeof EXPERIMENTS;

/** The literal union of variant names for one experiment. */
export type VariantOf<K extends ExperimentKey> = Extract<keyof (typeof EXPERIMENTS)[K]["variants"], string>;

/** The control arm is the first variant declared. */
export function controlVariantOf(key: ExperimentKey): string {
  return Object.keys(EXPERIMENTS[key].variants)[0];
}

/**
 * The variant declared at a zero-based position — what `?ab=<letter>` resolves
 * to (`a` -> 0). Declaration order, the same convention as `controlVariantOf`,
 * so reordering an experiment's variants changes what each letter forces.
 * Null when the experiment is unknown or has fewer variants than `index + 1`.
 */
export function variantAt(key: string, index: number): string | null {
  const definition = (EXPERIMENTS as Record<string, ExperimentDefinition | undefined>)[key];
  if (definition == null || !Number.isInteger(index) || index < 0) return null;
  return Object.keys(definition.variants)[index] ?? null;
}

export function isKnownVariant(key: string, variant: string): boolean {
  const definition = (EXPERIMENTS as Record<string, ExperimentDefinition | undefined>)[key];
  return definition != null && Object.prototype.hasOwnProperty.call(definition.variants, variant);
}

// The same two literals root.tsx and i18n.server.ts already key on. A route in the
// registry is written unprefixed; these are stripped before matching so one entry
// covers all three locales.
const LOCALE_PREFIXES = ["es", "fr-CA"];

/** `/fr-CA/products/drive` -> `/products/drive`; `/es` -> `/`; everything else unchanged. */
export function stripLocalePrefix(pathname: string): string {
  const segments = pathname.split("/");
  if (segments.length > 1 && LOCALE_PREFIXES.includes(segments[1])) {
    const rest = segments.slice(2).join("/");
    return `/${rest}`;
  }
  return pathname;
}

function normalizePathname(pathname: string): string {
  const withoutLocale = stripLocalePrefix(pathname);
  if (withoutLocale.length > 1 && withoutLocale.endsWith("/")) {
    return withoutLocale.slice(0, -1);
  }
  return withoutLocale;
}

/**
 * Whether an experiment is *exposed* on a pathname — i.e. whether a visitor on
 * this URL could actually see the variant. Not an assignment gate: assignment
 * happens on every document (see assign.server.ts for why). Locale-aware, so
 * one unprefixed entry covers /es and /fr-CA.
 */
export function matchesExperimentRoute(definition: ExperimentDefinition, pathname: string): boolean {
  const normalized = normalizePathname(pathname);
  return definition.routes.some((route) => normalizePathname(route) === normalized);
}

// Vite statically replaces this, so the check costs nothing at runtime and is the
// same constant in the worker bundle and the client bundle. Undefined under
// vitest, which is why draft experiments assign in unit tests.
const ALLOW_DRAFT = import.meta.env.VITE_APP_ENV !== "production";

/**
 * Whether *any* experiment would assign right now. The gate on minting a visitor
 * cookie at all: with nothing active there is nothing to be sticky about, so a
 * 180-day cookie would be tracking-shaped state serving no purpose.
 */
export function hasActiveExperiment(now: Date, options: { allowDraft?: boolean } = {}): boolean {
  return Object.values(EXPERIMENTS as Record<string, ExperimentDefinition>).some((definition) =>
    isExperimentActive(definition, now, options),
  );
}

export function isExperimentActive(
  definition: ExperimentDefinition,
  now: Date,
  options: { allowDraft?: boolean } = {},
): boolean {
  const allowDraft = options.allowDraft ?? ALLOW_DRAFT;

  if (definition.status === "paused" || definition.status === "completed") return false;
  if (definition.status === "draft" && !allowDraft) return false;

  const instant = now.getTime();
  if (definition.startsAt != null && instant < Date.parse(definition.startsAt)) return false;
  if (definition.endsAt != null && instant >= Date.parse(definition.endsAt)) return false;

  return true;
}
